import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getSettings, updateSettings, getUsers, createUser, updateUser, deleteUser } from '../services/api';
import { Permissions, defaultPermissions, canAccess } from '../permissions';

const allModules = [
  'students', 'prospects', 'leads', 'courses', 'lecturers',
  'finance', 'communication', 'tasks', 'reports', 'settings',
] as const;

const moduleLabels: Record<string, Record<string, string>> = {
  students: { he: 'סטודנטים', ar: 'الطلاب' },
  prospects: { he: 'מתעניינים', ar: 'المهتمون' },
  leads: { he: 'לידים', ar: 'العملاء المحتملون' },
  courses: { he: 'קורסים', ar: 'المساقات' },
  lecturers: { he: 'מרצים', ar: 'المحاضرون' },
  finance: { he: 'כספים', ar: 'المالية' },
  communication: { he: 'תקשורת', ar: 'التواصل' },
  tasks: { he: 'משימות', ar: 'المهام' },
  reports: { he: 'דוחות', ar: 'التقارير' },
  settings: { he: 'הגדרות', ar: 'الإعدادات' },
};

// Each module has different possible actions
const moduleActions: Record<string, string[]> = {
  students: ['view', 'add', 'edit', 'delete'],
  prospects: ['view', 'add', 'edit', 'delete'],
  leads: ['view', 'add', 'edit', 'delete'],
  courses: ['view', 'add', 'edit', 'delete'],
  lecturers: ['view', 'add', 'edit', 'delete'],
  finance: ['view', 'add', 'edit', 'delete'],
  communication: ['view', 'send'],
  tasks: ['view', 'add', 'edit', 'delete'],
  reports: ['view', 'export'],
  settings: ['view', 'edit', 'manageUsers'],
};

const actionLabels: Record<string, Record<string, string>> = {
  view: { he: 'צפייה', ar: 'عرض' },
  add: { he: 'הוספה', ar: 'إضافة' },
  edit: { he: 'עריכה', ar: 'تعديل' },
  delete: { he: 'מחיקה', ar: 'حذف' },
  send: { he: 'שליחה', ar: 'إرسال' },
  export: { he: 'ייצוא', ar: 'تصدير' },
  manageUsers: { he: 'ניהול משתמשים', ar: 'إدارة المستخدمين' },
};

const roleLabels: Record<string, Record<string, string>> = {
  admin: { he: 'מנהל', ar: 'مدير' },
  secretary: { he: 'מזכירה', ar: 'سكرتيرة' },
  lecturer: { he: 'מרצה', ar: 'محاضر' },
  viewer: { he: 'צופה', ar: 'مشاهد' },
};

const statusLabels: Record<string, Record<string, string>> = {
  active: { he: 'פעיל', ar: 'نشط' },
  disabled: { he: 'מושבת', ar: 'معطل' },
};

export default function Settings() {
  const { t, lang } = useLang();
  const { permissions: myPerms } = useUser();
  const [activeTab, setActiveTab] = useState('college');
  const [settings, setSettings] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [showPermissionsFor, setShowPermissionsFor] = useState<any | null>(null);
  const [editPerms, setEditPerms] = useState<Permissions | null>(null);
  const [userForm, setUserForm] = useState({
    username: '', password: '', name: '', nameAr: '', role: 'secretary',
    email: '', phone: '', status: 'active',
  });

  const slang = lang === 'ar' ? 'ar' : 'he';

  const loadUsers = () => {
    getUsers().then(res => setUsers(res.data)).catch(() => {});
  };

  useEffect(() => {
    getSettings().then(res => setSettings(res.data)).catch(() => {});
    loadUsers();
  }, []);

  const handleSaveSettings = async () => {
    await updateSettings(settings);
    alert(lang === 'ar' ? 'تم حفظ الإعدادات بنجاح' : 'ההגדרות נשמרו בהצלחה');
  };

  const handleAddUser = async () => {
    const perms = defaultPermissions[userForm.role] || defaultPermissions.viewer;
    await createUser({ ...userForm, permissions: perms });
    setShowUserModal(false);
    setUserForm({ username: '', password: '', name: '', nameAr: '', role: 'secretary', email: '', phone: '', status: 'active' });
    loadUsers();
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    await updateUser(editingUser.id, editingUser);
    setEditingUser(null);
    loadUsers();
  };

  const handleDeleteUser = async (id: string) => {
    if (window.confirm(lang === 'ar' ? 'هل تريد حذف هذا المستخدم؟' : 'האם למחוק משתמש זה?')) {
      await deleteUser(id);
      loadUsers();
    }
  };

  const handleToggleStatus = async (user: any) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active';
    await updateUser(user.id, { status: newStatus });
    loadUsers();
  };

  // Permissions editor
  const openPermissions = (user: any) => {
    setShowPermissionsFor(user);
    setEditPerms(user.permissions || defaultPermissions[user.role] || defaultPermissions.viewer);
  };

  const togglePermission = (module: string, action: string) => {
    if (!editPerms) return;
    const mod = { ...(editPerms as any)[module] };
    mod[action] = !mod[action];
    setEditPerms({ ...editPerms, [module]: mod });
  };

  const handleSavePermissions = async () => {
    if (!showPermissionsFor || !editPerms) return;
    await updateUser(showPermissionsFor.id, { permissions: editPerms });
    setShowPermissionsFor(null);
    setEditPerms(null);
    loadUsers();
  };

  const handleResetPermissions = () => {
    if (!showPermissionsFor) return;
    const role = showPermissionsFor.role || 'viewer';
    setEditPerms(JSON.parse(JSON.stringify(defaultPermissions[role] || defaultPermissions.viewer)));
  };

  const canManageUsers = canAccess(myPerms, 'settings', 'manageUsers');

  if (!settings) return <div className="page-section"><p>{t('loading')}</p></div>;

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">⚙️ {t('systemSettings')}</span>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'college' ? 'active' : ''}`} onClick={() => setActiveTab('college')}>{t('collegeSettings')}</button>
        {canManageUsers && (
          <button className={`tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>{t('userManagement')}</button>
        )}
        <button className={`tab ${activeTab === 'semesters' ? 'active' : ''}`} onClick={() => setActiveTab('semesters')}>{t('semesterManagement')}</button>
        <button className={`tab ${activeTab === 'billing' ? 'active' : ''}`} onClick={() => setActiveTab('billing')}>🔌 {t('externalConnections')}</button>
      </div>

      {activeTab === 'college' && (
        <div className="page-section">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('collegeName')} (עברית)</label>
              <input className="form-input" value={settings.collegeName} onChange={e => setSettings({...settings, collegeName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('collegeName')} (ערבית)</label>
              <input className="form-input" value={settings.collegeNameAr} onChange={e => setSettings({...settings, collegeNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('collegeName')} (English)</label>
              <input className="form-input" value={settings.collegeNameEn} onChange={e => setSettings({...settings, collegeNameEn: e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <input className="form-input" type="tel" value={settings.phone} onChange={e => setSettings({...settings, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <input className="form-input" type="email" value={settings.email} onChange={e => setSettings({...settings, email: e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('address')}</label>
            <input className="form-input" value={settings.address} onChange={e => setSettings({...settings, address: e.target.value})} />
          </div>

          <h4 style={{ color: '#7B1FA2', marginTop: 20, marginBottom: 12 }}>{t('branches')}</h4>
          {settings.branches?.map((branch: any, i: number) => (
            <div className="form-row" key={i}>
              <div className="form-group">
                <label className="form-label">{t('name')}</label>
                <input className="form-input" value={branch.name} onChange={e => {
                  const b = [...settings.branches];
                  b[i] = {...b[i], name: e.target.value};
                  setSettings({...settings, branches: b});
                }} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('address')}</label>
                <input className="form-input" value={branch.address} onChange={e => {
                  const b = [...settings.branches];
                  b[i] = {...b[i], address: e.target.value};
                  setSettings({...settings, branches: b});
                }} />
              </div>
            </div>
          ))}
          <button className="btn btn-sm btn-outline" style={{ marginBottom: 16 }} onClick={() => setSettings({...settings, branches: [...settings.branches, { name: '', address: '' }]})}>
            + {lang === 'ar' ? 'إضافة فرع' : 'הוסף סניף'}
          </button>

          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSaveSettings}>{t('save')}</button>
          </div>
        </div>
      )}

      {activeTab === 'users' && canManageUsers && (
        <div className="page-section">
          <div style={{ marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => setShowUserModal(true)}>
              + {lang === 'ar' ? 'إضافة مستخدم' : 'הוסף משתמש'}
            </button>
          </div>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('username')}</th>
                <th>{t('name')}</th>
                <th>{t('role')}</th>
                <th>{t('email')}</th>
                <th>{t('phone')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ direction: 'ltr', textAlign: 'left' }}>{u.username}</td>
                  <td>{lang === 'ar' ? u.nameAr || u.name : u.name}</td>
                  <td>
                    <span className={`badge ${
                      u.role === 'admin' ? 'badge-green' :
                      u.role === 'secretary' ? 'badge-blue' :
                      u.role === 'lecturer' ? 'badge-yellow' : 'badge-gray'
                    }`}>
                      {roleLabels[u.role]?.[slang] || u.role}
                    </span>
                  </td>
                  <td dir="ltr">{u.email}</td>
                  <td dir="ltr">{u.phone || '-'}</td>
                  <td>
                    <span
                      className={`badge ${u.status === 'active' ? 'badge-green' : 'badge-red'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggleStatus(u)}
                      title={lang === 'ar' ? 'اضغط لتغيير الحالة' : 'לחץ לשינוי סטטוס'}
                    >
                      {statusLabels[u.status]?.[slang] || u.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-primary" onClick={() => setEditingUser({ ...u })}>
                        ✏️ {t('edit')}
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={() => openPermissions(u)}>
                        🔐 {lang === 'ar' ? 'الصلاحيات' : 'הרשאות'}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(u.id)}>
                        🗑️ {t('delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Create User Modal */}
          {showUserModal && (
            <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
                <div className="modal-header">
                  <h3 className="modal-title">👤 {lang === 'ar' ? 'إضافة مستخدم جديد' : 'הוסף משתמש חדש'}</h3>
                  <button className="modal-close" onClick={() => setShowUserModal(false)}>×</button>
                </div>
                <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('name')} (עברית) *</label>
                      <input className="form-input" value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('name')} (ערבית)</label>
                      <input className="form-input" value={userForm.nameAr} onChange={e => setUserForm({...userForm, nameAr: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('username')} *</label>
                      <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }} value={userForm.username} onChange={e => setUserForm({...userForm, username: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('password')} *</label>
                      <input className="form-input" type="password" style={{ direction: 'ltr', textAlign: 'left' }} value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('email')}</label>
                      <input className="form-input" type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('phone')}</label>
                      <input className="form-input" type="tel" value={userForm.phone} onChange={e => setUserForm({...userForm, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('role')} *</label>
                      <select className="form-select" value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})}>
                        <option value="admin">{roleLabels.admin[slang]}</option>
                        <option value="secretary">{roleLabels.secretary[slang]}</option>
                        <option value="lecturer">{roleLabels.lecturer[slang]}</option>
                        <option value="viewer">{roleLabels.viewer[slang]}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('status')}</label>
                      <select className="form-select" value={userForm.status} onChange={e => setUserForm({...userForm, status: e.target.value})}>
                        <option value="active">{statusLabels.active[slang]}</option>
                        <option value="disabled">{statusLabels.disabled[slang]}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={handleAddUser} disabled={!userForm.username || !userForm.password || !userForm.name}>
                    💾 {t('save')}
                  </button>
                  <button className="btn btn-outline" onClick={() => setShowUserModal(false)}>{t('cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {/* Edit User Modal */}
          {editingUser && (
            <div className="modal-overlay" onClick={() => setEditingUser(null)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
                <div className="modal-header">
                  <h3 className="modal-title">✏️ {lang === 'ar' ? 'تعديل مستخدم' : 'עריכת משתמש'}</h3>
                  <button className="modal-close" onClick={() => setEditingUser(null)}>×</button>
                </div>
                <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('name')} (עברית)</label>
                      <input className="form-input" value={editingUser.name || ''} onChange={e => setEditingUser({...editingUser, name: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('name')} (ערבית)</label>
                      <input className="form-input" value={editingUser.nameAr || ''} onChange={e => setEditingUser({...editingUser, nameAr: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('username')}</label>
                      <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }} value={editingUser.username || ''} onChange={e => setEditingUser({...editingUser, username: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{lang === 'ar' ? 'كلمة مرور جديدة' : 'סיסמה חדשה'}</label>
                      <input className="form-input" type="password" style={{ direction: 'ltr', textAlign: 'left' }} placeholder={lang === 'ar' ? 'اتركه فارغاً للإبقاء' : 'השאר ריק לשמירה'} onChange={e => { if (e.target.value) setEditingUser({...editingUser, password: e.target.value}); }} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('email')}</label>
                      <input className="form-input" type="email" value={editingUser.email || ''} onChange={e => setEditingUser({...editingUser, email: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('phone')}</label>
                      <input className="form-input" type="tel" value={editingUser.phone || ''} onChange={e => setEditingUser({...editingUser, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('role')}</label>
                      <select className="form-select" value={editingUser.role} onChange={e => setEditingUser({...editingUser, role: e.target.value})}>
                        <option value="admin">{roleLabels.admin[slang]}</option>
                        <option value="secretary">{roleLabels.secretary[slang]}</option>
                        <option value="lecturer">{roleLabels.lecturer[slang]}</option>
                        <option value="viewer">{roleLabels.viewer[slang]}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('status')}</label>
                      <select className="form-select" value={editingUser.status} onChange={e => setEditingUser({...editingUser, status: e.target.value})}>
                        <option value="active">{statusLabels.active[slang]}</option>
                        <option value="disabled">{statusLabels.disabled[slang]}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={handleUpdateUser}>💾 {t('save')}</button>
                  <button className="btn btn-outline" onClick={() => setEditingUser(null)}>{t('cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {/* Permissions Matrix Modal */}
          {showPermissionsFor && editPerms && (
            <div className="modal-overlay" onClick={() => { setShowPermissionsFor(null); setEditPerms(null); }}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 750 }}>
                <div className="modal-header">
                  <h3 className="modal-title">
                    🔐 {lang === 'ar' ? 'صلاحيات' : 'הרשאות'}: {lang === 'ar' ? showPermissionsFor.nameAr || showPermissionsFor.name : showPermissionsFor.name}
                    <span style={{ fontSize: 13, color: '#666', marginRight: 8 }}>
                      ({roleLabels[showPermissionsFor.role]?.[slang] || showPermissionsFor.role})
                    </span>
                  </h3>
                  <button className="modal-close" onClick={() => { setShowPermissionsFor(null); setEditPerms(null); }}>×</button>
                </div>
                <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
                  <table className="data-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 100 }}>{lang === 'ar' ? 'الوحدة' : 'מודול'}</th>
                        <th style={{ textAlign: 'center' }}>{actionLabels.view[slang]}</th>
                        <th style={{ textAlign: 'center' }}>{actionLabels.add[slang]}</th>
                        <th style={{ textAlign: 'center' }}>{actionLabels.edit[slang]}</th>
                        <th style={{ textAlign: 'center' }}>{actionLabels.delete[slang]}</th>
                        <th style={{ textAlign: 'center' }}>{lang === 'ar' ? 'خاص' : 'מיוחד'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allModules.map(mod => {
                        const actions = moduleActions[mod];
                        const modPerms = (editPerms as any)[mod] || {};
                        return (
                          <tr key={mod}>
                            <td style={{ fontWeight: 600 }}>{moduleLabels[mod]?.[slang] || mod}</td>
                            <td style={{ textAlign: 'center' }}>
                              {actions.includes('view') && (
                                <input type="checkbox" checked={!!modPerms.view}
                                  onChange={() => togglePermission(mod, 'view')}
                                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {actions.includes('add') && (
                                <input type="checkbox" checked={!!modPerms.add}
                                  onChange={() => togglePermission(mod, 'add')}
                                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {actions.includes('edit') && (
                                <input type="checkbox" checked={!!modPerms.edit}
                                  onChange={() => togglePermission(mod, 'edit')}
                                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {actions.includes('delete') && (
                                <input type="checkbox" checked={!!modPerms.delete}
                                  onChange={() => togglePermission(mod, 'delete')}
                                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {actions.includes('send') && (
                                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 12 }}>
                                  <input type="checkbox" checked={!!modPerms.send}
                                    onChange={() => togglePermission(mod, 'send')}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                                  {actionLabels.send[slang]}
                                </label>
                              )}
                              {actions.includes('export') && (
                                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 12 }}>
                                  <input type="checkbox" checked={!!modPerms.export}
                                    onChange={() => togglePermission(mod, 'export')}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                                  {actionLabels.export[slang]}
                                </label>
                              )}
                              {actions.includes('manageUsers') && (
                                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 12 }}>
                                  <input type="checkbox" checked={!!modPerms.manageUsers}
                                    onChange={() => togglePermission(mod, 'manageUsers')}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                                  {actionLabels.manageUsers[slang]}
                                </label>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={handleSavePermissions}>
                    💾 {t('save')}
                  </button>
                  <button className="btn btn-outline" onClick={handleResetPermissions}>
                    🔄 {lang === 'ar' ? 'استعادة الافتراضي' : 'שחזר לברירת מחדל'}
                  </button>
                  <button className="btn btn-outline" onClick={() => { setShowPermissionsFor(null); setEditPerms(null); }}>
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'semesters' && (
        <div className="page-section">
          <div className="form-row" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">{lang === 'ar' ? 'الفصل الحالي' : 'סמסטר נוכחי'}</label>
              <select className="form-select" value={settings.currentSemester} onChange={e => setSettings({...settings, currentSemester: e.target.value})}>
                <option value="A">{lang === 'ar' ? 'الفصل الأول' : 'סמסטר א׳'}</option>
                <option value="B">{lang === 'ar' ? 'الفصل الثاني' : 'סמסטר ב׳'}</option>
                <option value="summer">{lang === 'ar' ? 'صيفي' : 'קיץ'}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('academicYear')}</label>
              <input className="form-input" value={settings.currentYear} onChange={e => setSettings({...settings, currentYear: e.target.value})} />
            </div>
          </div>

          <h4 style={{ color: '#7B1FA2', marginBottom: 12 }}>{lang === 'ar' ? 'الفصول المحددة' : 'סמסטרים מוגדרים'}</h4>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{lang === 'ar' ? 'البداية' : 'תחילה'}</th>
                <th>{lang === 'ar' ? 'النهاية' : 'סיום'}</th>
              </tr>
            </thead>
            <tbody>
              {settings.semesters?.map((s: any) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.startDate}</td>
                  <td>{s.endDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSaveSettings}>{t('save')}</button>
          </div>
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="page-section">
          {/* Shaked Settings */}
          <h4 style={{ color: '#7B1FA2', marginBottom: 12 }}>🏦 {t('shakedSettings')}</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('terminalNumber')}</label>
              <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.shakedTerminal || ''}
                onChange={e => setSettings({...settings, shakedTerminal: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('apiKey')}</label>
              <input className="form-input" type="password" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.shakedApiKey || ''}
                onChange={e => setSettings({...settings, shakedApiKey: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('apiUrl')}</label>
              <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.shakedApiUrl || ''}
                onChange={e => setSettings({...settings, shakedApiUrl: e.target.value})}
                placeholder="https://api.shaked.co.il" />
            </div>
          </div>
          <button className="btn btn-outline" style={{ marginBottom: 24 }}
            onClick={() => alert(settings.shakedTerminal ? t('connectionSuccess') : t('connectionFailed'))}>
            🔌 {t('testConnection')}
          </button>

          {/* PlaCard Settings */}
          <h4 style={{ color: '#7B1FA2', marginBottom: 12 }}>💳 {t('placardSettings')}</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('terminalNumber')}</label>
              <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.placardTerminal || ''}
                onChange={e => setSettings({...settings, placardTerminal: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('apiKey')}</label>
              <input className="form-input" type="password" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.placardApiKey || ''}
                onChange={e => setSettings({...settings, placardApiKey: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('apiUrl')}</label>
              <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.placardApiUrl || ''}
                onChange={e => setSettings({...settings, placardApiUrl: e.target.value})}
                placeholder="https://api.placard.co.il" />
            </div>
          </div>
          <button className="btn btn-outline" style={{ marginBottom: 24 }}
            onClick={() => alert(settings.placardTerminal ? t('connectionSuccess') : t('connectionFailed'))}>
            🔌 {t('testConnection')}
          </button>

          {/* VoicEnter Settings */}
          <h4 style={{ color: '#7B1FA2', marginBottom: 12, marginTop: 24 }}>📞 {t('voicenterSettings')}</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('voicenterCustomerId')}</label>
              <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.voicenterCustomerId || ''}
                onChange={e => setSettings({...settings, voicenterCustomerId: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('voicenterApiKey')}</label>
              <input className="form-input" type="password" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.voicenterApiKey || ''}
                onChange={e => setSettings({...settings, voicenterApiKey: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('voicenterApiUrl')}</label>
              <input className="form-input" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.voicenterApiUrl || 'https://api.voicenter.com'}
                onChange={e => setSettings({...settings, voicenterApiUrl: e.target.value})}
                placeholder="https://api.voicenter.com" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('voicenterMainPhone')}</label>
              <input className="form-input" type="tel" style={{ direction: 'ltr', textAlign: 'left' }}
                value={settings.voicenterMainPhone || ''}
                onChange={e => setSettings({...settings, voicenterMainPhone: e.target.value})}
                placeholder="03-1234567" />
            </div>
          </div>
          <button className="btn btn-outline" style={{ marginBottom: 24 }}
            onClick={() => alert(settings.voicenterCustomerId ? t('connectionSuccess') : t('connectionFailed'))}>
            🔌 {t('testConnection')}
          </button>

          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSaveSettings}>{t('save')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
